import React, { useState, useEffect } from 'react';
import { Button, Grid, PageHeader, Row } from 'react-bootstrap';
import DatePicker from 'react-date-picker';
import BaseTable from 'react-base-table';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import FuzzySearch from 'fuzzy-search';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { Auth } from 'aws-amplify';
import yyyymmdd from '../libs/date_convert';
import {
  CreateDriverActionMutation,
  GetDriverAction,
  GetLocationRoutes,
  GetOpsLocationsTypeOnly,
  OpsLocations,
  GetPackingSlipsV3Paginated,
} from '../libs/GraphQL';
import {
  getDropOffSummaryColumns,
  DROP_REASONS,
} from '../constants/DropOffSummary';
import ConfirmationModal from '../components/ConfirmationModal';
import 'react-toastify/dist/ReactToastify.css';
import './ViewDropOffSummary.css';
import DropOverrideBulkUpdateModal from '../components/BulkUpdateModal/DropOverrideBulkUpdateModal';
import Loader from '../components/atoms/Loader';

const ViewDropOffSummary = () => {
  const [date, setDate] = useState({
    current: new Date(),
    search: yyyymmdd(new Date(), '-'),
  });
  const [data, setData] = useState({
    packingSlip: [],
    locationRoutes: [],
    result: [],
    initResult: [],
  });
  const [showData, setShowData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [overrideDisabled, setOverrideDisabled] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bulkUpdateDisabled, setBulkUpdateDisabled] = useState(true);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [checkedBulkItemsCount, setCheckedBulkItemsCount] = useState(0);
  const [modifiedStatusItemsCount, setModifiedStatusItemsCount] = useState(0);
  const [sortBy, setSortBy] = useState({});
  const [searchbarValue, setSearchbarValue] = useState('');
  const [routeSearchValue, setRouteSearchValue] = useState('');
  const [wholesaleLocations, setWholesaleLocations] = useState([]);
  const [fridgeLocations, setFridgeLocations] = useState([]);
  const [showUndoConfirmModal, setShowUndoConfirmModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalConfig, setResultModalConfig] = useState({
    title: '',
    message: '',
    isError: false,
    showContinueAnyway: false,
  });
  const [itemToUndo, setItemToUndo] = useState(null);

  // Use ref to track current data.result for synchronous access
  const dataResultRef = React.useRef([]);

  const history = useHistory();

  useEffect(() => {
    const LDCUser = {
      key: Auth.user.attributes.email,
    };

    const ldclient = LDClient.initialize(
      process.env.REACT_APP_LAUNCH_DARKLY_CMS,
      LDCUser,
    );

    ldclient.on('ready', () => {
      const allFlagsValue = ldclient.allFlags();

      if (!allFlagsValue['drop-override']) {
        // redirect to home if user haven't access to see this page
        history.push('/home');
      }
    });
  }, []);

  useEffect(() => {
    const getOpsLocations = async () => {
      try {
        const allLocations = [];
        let nextToken = null;

        do {
          const response = await GetOpsLocationsTypeOnly(nextToken, ['LIVE']);
          if (response?.data?.opsLocations) {
            const locations = response.data.opsLocations.locations || [];
            allLocations.push(...locations);
          }
          nextToken = response?.data?.opsLocations?.nextToken;
        } while (nextToken);

        const wholesales = allLocations
          .filter((item) => item.type === 'WHOLESALE')
          .map((item) => item.id);
        const fridges = allLocations
          .filter((item) => item.type === 'FRIDGE')
          .map((item) => item.id);
        setWholesaleLocations(wholesales);
        setFridgeLocations(fridges);
      } catch (e) {
        console.error(e);
        toast.error(
          `Failed fetching GetOpsLocationsTypeOnly: ${JSON.parse(e)} `,
        );
      } finally {
        setIsInitLoading(false);
      }
    };

    getOpsLocations();
  }, []);

  const handleDateChange = (value) => {
    setDate({
      current: value,
      search: yyyymmdd(value, '-'),
    });
  };

  const fetchAllPackingSlips = async (filter) => {
    let items = [];
    let nextToken = null;

    try {
      do {
        const response = await GetPackingSlipsV3Paginated(filter, nextToken);

        if (response?.data?.getPackingSlipsV3) {
          items = items.concat(
            response.data.getPackingSlipsV3.packingSlips || [],
          );
          nextToken = response.data.getPackingSlipsV3.nextToken;
        } else {
          // If response is incomplete, break out of the loop
          break;
        }
      } while (nextToken);
    } catch (error) {
      console.error('Error fetching paginated packing slips:', error);
      // Return whatever items we managed to fetch
    }

    return items;
  };

  const packingSlipsToIgnore = (item, unreachableLocations) => {
    return !(
      item.type === 'INTERNAL_ORDER' ||
      item.clientCode === 'ThirdParty' ||
      item.clientCode === 'MenuRD' ||
      item.clientCode === 'Pulping' ||
      item.status === 'DELETED' ||
      unreachableLocations.includes(item.locationId)
    );
  };

  const handleSearch = async () => {
    setIsLoading(true);

    try {
      // Fetch all data in parallel
      const [
        packingSlipsData,
        locationRoutesResponse,
        dropOverrideResponse,
        locationUnreachableData,
      ] = await Promise.all([
        fetchAllPackingSlips({ dropOffDate: date.search }),
        GetLocationRoutes({ stockingDate: date.search }),
        GetDriverAction({ eventType: 'DropOverride', createdAt: date.search }),
        GetDriverAction({
          eventType: 'LocationUnreachable',
          createdAt: date.search,
        }),
      ]);

      const locationRoutes = locationRoutesResponse.routes;
      const getDriverAction = dropOverrideResponse.data.getDriverAction;
      const opsResponse = await OpsLocations({ status: ['LIVE'] });
      const opsLocations = opsResponse?.data?.opsLocations?.locations || [];

      const amazonGos = opsLocations
        .map((location) => {
          if (location.id.includes('AMAZON_GO')) {
            return location;
          }
          return undefined;
        })
        .filter((item) => item !== undefined);

      const unreachableLocations =
        locationUnreachableData.data.getDriverAction.actions.map(
          (item) => item.locationId,
        );

      let getPackingSlipsV3 = packingSlipsData.filter((item) =>
        packingSlipsToIgnore(item, unreachableLocations),
      );

      getPackingSlipsV3 = getPackingSlipsV3.filter((packingSlip) => {
        /*
        locationroute exists, stockedTime exists= don't return packingslip
        locationroute exists, stockedTime does not exist= return packingslip
        locaionroute does not exist= return packingslip
        */
        let found = false;
        const shouldShowOnOverrideTable = locationRoutes.some(
          (locationRoute) => {
            // Guarentee we are comparing the same types
            if (
              packingSlip.locationId === locationRoute.id &&
              Number(packingSlip.stockingIndex) ===
                locationRoute.stockingIndex &&
              packingSlip.dropOffDate === locationRoute.stockingDate
            ) {
              if (locationRoute.stockedTimeUTC === null) {
                // only add items to dropOverride if they have no stockedTimeUTC
                return true;
              }
              found = true;
              return false;
            }
            return false;
          },
        );
        return shouldShowOnOverrideTable !== false || !found;
      });

      getPackingSlipsV3 = getPackingSlipsV3.map((packingSlip) => {
        const isWholesale = wholesaleLocations.includes(packingSlip.locationId);
        const isFridge = fridgeLocations.includes(packingSlip.locationId);
        const locationType = isWholesale
          ? 'WHOLESALE'
          : isFridge
          ? 'FRIDGE'
          : 'UNKNOWN';

        return {
          ...packingSlip,
          locationType,
        };
      });

      // Extend uniqueId to pass it into DriverAction to use it for ChainOfCustody table
      getPackingSlipsV3 = getPackingSlipsV3.map((packingSlip) => {
        const extendedLocationId = `${packingSlip.locationId}&${
          packingSlip.uniqueId.split('#')[1]
        }`;

        const updatedCreateDate = packingSlip.createDate.split('-').join('/');

        const extendedUniqueId = `${updatedCreateDate}#${extendedLocationId}#${Math.round(
          packingSlip.stockingIndex,
        )}`;

        return {
          ...packingSlip,
          extendedUniqueId,
        };
      });

      getPackingSlipsV3 = getPackingSlipsV3.map((packingSlip) => {
        const displayLocationId =
          amazonGos.find((location) => {
            return location.id === packingSlip.locationId;
          })?.opportunityName || packingSlip.locationId;

        return {
          ...packingSlip,
          displayLocationId,
        };
      });

      // add dropStatus data and checked field to final items
      const result = getPackingSlipsV3.map((packingSlip) => {
        // Find ALL matching DriverActions for this packing slip
        const matchingActions = getDriverAction.actions.filter(
          (driverAction) =>
            driverAction.locationId === packingSlip.locationId &&
            JSON.parse(driverAction.data).packingSlipId ===
              packingSlip.extendedUniqueId,
        );

        // Sort by _lastModified descending (most recent first) and take the first one
        const itemWithDropStatus = matchingActions.sort(
          (a, b) => b._lastModified - a._lastModified,
        )[0];

        const itemDropStatusData = itemWithDropStatus
          ? JSON.parse(itemWithDropStatus.data)
          : undefined;

        const dropStatus = itemDropStatusData
          ? itemDropStatusData.dropStatus
          : 'Pending';

        // Keep user email for audit trail, but show special reason for undone items
        const statusReason = itemDropStatusData
          ? itemDropStatusData.statusReason
          : '';

        const userSubmitted = itemDropStatusData
          ? itemDropStatusData.userSubmitted
          : '';

        // Capture timestamp and determine if item was previously Complete/Not Delivering
        // Convert from seconds to milliseconds if needed (timestamps before year 2000 are likely in seconds)
        // Year 2000 in seconds: 946684800, in milliseconds: 946684800000
        const lastModified = itemWithDropStatus
          ? itemWithDropStatus._lastModified < 946684800000
            ? itemWithDropStatus._lastModified * 1000
            : itemWithDropStatus._lastModified
          : null;

        // If current status is Pending but there's a lastModified timestamp and itemDropStatusData,
        // it means this item was previously set to Complete/Not Delivering and then undone
        const wasCompleted =
          dropStatus === 'Pending' && lastModified && itemDropStatusData;

        return {
          ...packingSlip,
          dropStatus,
          statusReason,
          dropStatusToOverride: 'Pending',
          userSubmitted,
          checked: false,
          lastModified,
          wasCompleted,
        };
      });

      setData({
        packingSlip: getPackingSlipsV3,
        locationRoutes,
        initResult: result,
        result,
      });

      // Update ref with new search results to ensure undo validation uses current data
      dataResultRef.current = result;

      setSearchbarValue('');
      setRouteSearchValue('');

      setShowData(true);
    } catch (error) {
      console.error('Error loading drop off summary data:', error);
      toast.error(
        'Failed to load drop off summary data. Please try again or contact support if the issue persists.',
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          theme: 'light',
        },
      );
    } finally {
      // Always reset loading state, even if an error occurs
      setIsLoading(false);
    }
  };

  const handleSearchClear = () => {
    setShowData(false);
    setSearchbarValue('');
    setRouteSearchValue('');
    setData((prevState) => ({
      ...prevState,
      result: [],
    }));
  };

  const calcCountOfChangedItems = (dataList) => {
    let atLeastOneChecked = false;
    let checkedCount = 0;

    if (dataList.length) {
      dataList.forEach((currentItem) => {
        if (currentItem.dropStatusToOverride !== 'Pending') {
          checkedCount += 1;
          atLeastOneChecked = true;
        }
      });
    }

    setOverrideDisabled(!atLeastOneChecked);
    setModifiedStatusItemsCount(checkedCount);
  };

  const confirmOverride = async (isBulk = false, bulkStatus, bulkReason) => {
    setIsLoading(true);
    const itemsToOverride = [];
    let modifiedInitResult = data.initResult;
    const checkedItems = isBulk
      ? checkedBulkItemsCount
      : modifiedStatusItemsCount;
    let modifiedResult;

    if (isBulk) {
      modifiedResult = data.result.map((item) => {
        if (item.checked) {
          itemsToOverride.push(item);

          return {
            ...item,
            dropStatus: bulkStatus,
            statusReason: bulkReason,
            dropStatusToOverride: 'Pending',
            userSubmitted: localStorage.getItem('userEmail'),
            checked: false,
          };
        }

        return item;
      });
    } else {
      modifiedResult = data.result.map((item) => {
        if (item.dropStatusToOverride !== 'Pending') {
          itemsToOverride.push(item);

          return {
            ...item,
            dropStatus: item.dropStatusToOverride,
            dropStatusToOverride: 'Pending',
            userSubmitted: localStorage.getItem('userEmail'),
            checked: false,
          };
        }

        return item;
      });
    }

    if (searchbarValue) {
      // Show correct dropStatus after filter changing
      modifiedInitResult = modifiedInitResult.map((item) => {
        const itemToUpdate = itemsToOverride.find(
          (itemToOverride) =>
            item.locationId === itemToOverride.locationId &&
            item.extendedUniqueId === itemToOverride.extendedUniqueId,
        );

        if (itemToUpdate) {
          return {
            ...item,
            dropStatus: isBulk ? bulkStatus : itemToUpdate.dropStatusToOverride,
            dropStatusToOverride: 'Pending',
            userSubmitted: localStorage.getItem('userEmail'),
            checked: false,
          };
        }

        return item;
      });
    }

    try {
      await Promise.all(
        itemsToOverride.map((item) => {
          return CreateDriverActionMutation({
            data: JSON.stringify({
              userSubmitted: localStorage.getItem('userEmail'),
              dropStatus: isBulk ? bulkStatus : item.dropStatusToOverride,
              statusReason: isBulk ? bulkReason : item.statusReason,
              originalDropOffDate: date.search,
              packingSlipId: item.extendedUniqueId,
            }),
            eventType: 'DropOverride',
            locationId: item.locationId,
          });
        }),
      );

      setData((prevState) => ({
        ...prevState,
        initResult: modifiedInitResult,
        result: modifiedResult,
      }));

      // Update ref to sync with confirmed changes
      dataResultRef.current = modifiedResult;

      setShowConfirmModal(false);
      setOverrideDisabled(true);
      setShowBulkUpdateModal(false);
      setBulkUpdateDisabled(true);

      toast.success(
        `Successfully updated drop status for ${checkedItems} record${
          checkedItems > 1 ? 's' : ''
        }`,
        {
          className: 'prettyToast',
          position: 'top-center',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          theme: 'light',
        },
      );
    } catch (e) {
      console.error(e);
    }

    setIsLoading(false);
  };

  // Undo functionality handlers
  const onClickUndo = (item) => {
    // Use ref for synchronous access to the latest data
    // This avoids React state update timing issues
    const currentResult =
      dataResultRef.current.length > 0 ? dataResultRef.current : data.result;

    const hasPendingChanges = currentResult.some(
      (dataItem) =>
        dataItem.dropStatusToOverride !== 'Pending' &&
        dataItem.dropStatus === 'Pending',
    );

    if (hasPendingChanges) {
      // Show warning modal - user must complete or discard pending changes first
      // Store the item so "Continue Anyway" can proceed with undo
      setItemToUndo(item);
      setResultModalConfig({
        title: 'Pending Changes Detected',
        message:
          'You have unsaved changes on other records. Please complete or discard these changes before undoing a completed drop.',
        isError: false,
        showContinueAnyway: true,
      });
      setShowResultModal(true);
    } else {
      // No pending changes - show normal undo confirmation
      setItemToUndo(item);
      setShowUndoConfirmModal(true);
    }
  };

  const handleConfirmUndo = async () => {
    if (!itemToUndo) return;

    setShowUndoConfirmModal(false);
    setIsLoading(true);

    try {
      const payload = {
        data: JSON.stringify({
          userSubmitted: localStorage.getItem('userEmail'),
          dropStatus: 'Pending',
          statusReason: '', // Empty string for Pending status (backend may reject custom values)
          originalDropOffDate: date.search,
          packingSlipId: itemToUndo.extendedUniqueId,
        }),
        eventType: 'DropOverride',
        locationId: itemToUndo.locationId,
      };

      await CreateDriverActionMutation(payload);

      // Update local state to reflect the undo without refetching
      const modifiedResult = data.result.map((item) => {
        if (
          item.locationId === itemToUndo.locationId &&
          item.extendedUniqueId === itemToUndo.extendedUniqueId
        ) {
          return {
            ...item,
            dropStatus: 'Pending',
            dropStatusToOverride: 'Pending',
            statusReason: '',
            userSubmitted: localStorage.getItem('userEmail'),
            wasCompleted: true,
            lastModified: Date.now(),
          };
        }
        return item;
      });

      const modifiedInitResult = data.initResult.map((item) => {
        if (
          item.locationId === itemToUndo.locationId &&
          item.extendedUniqueId === itemToUndo.extendedUniqueId
        ) {
          return {
            ...item,
            dropStatus: 'Pending',
            dropStatusToOverride: 'Pending',
            statusReason: '',
            userSubmitted: localStorage.getItem('userEmail'),
            wasCompleted: true,
            lastModified: Date.now(),
          };
        }
        return item;
      });

      setData((prevState) => ({
        ...prevState,
        initResult: modifiedInitResult,
        result: modifiedResult,
      }));

      // Update ref to sync with undo changes
      dataResultRef.current = modifiedResult;

      toast.success(
        `Drop for location ${itemToUndo.displayLocationId} has been successfully reverted to Pending.`,
        {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } catch (error) {
      console.error('Error undoing drop status:', error);

      toast.error(
        'Failed to undo drop status. Please try again or refresh the page if the issue persists.',
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } finally {
      setIsLoading(false);
      setItemToUndo(null);
    }
  };

  const handleCancelUndo = () => {
    setShowUndoConfirmModal(false);
    setItemToUndo(null);
  };

  // Format timestamp for display
  const formatTimestamp = (epochMilliseconds) => {
    if (!epochMilliseconds) return null;

    const now = Date.now();
    const diff = now - epochMilliseconds;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    // Relative time for recent changes (< 24 hours)
    if (days === 0) {
      if (hours === 0) {
        if (minutes === 0) return 'Just now';
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }

    // Absolute date/time for older changes
    const dateObj = new Date(epochMilliseconds);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hour = dateObj.getHours();
    const minute = String(dateObj.getMinutes()).padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;

    return `${month}/${day}/${year} ${displayHour}:${minute} ${ampm}`;
  };

  const onChangeTableEvent = (e) => {
    if (e.target.classList.contains('statusDropdown')) {
      onChangeDropStatus(e);
    }

    if (e.target.classList.contains('statusReason')) {
      onChangeReason(e);
    }
  };

  const onClickTableEvent = (e) => {
    if (e.target.classList.contains('checkbox')) {
      onClickCheckbox(e);
    }
  };

  const onChangeDropStatus = (e) => {
    const newDropStatusToOverride = e.target.value;

    const modifiedResult = data.result.map((item) => {
      if (
        item.locationId === e.target.getAttribute('data-locationid') &&
        item.extendedUniqueId === e.target.getAttribute('data-packingslipid')
      ) {
        return {
          ...item,
          statusReason:
            e.target.value === 'Pending'
              ? ''
              : item.statusReason || DROP_REASONS[e.target.value][0].value,
          dropStatusToOverride: newDropStatusToOverride,
        };
      }

      return item;
    });

    // Update ref immediately for synchronous access
    dataResultRef.current = modifiedResult;

    setData((prevState) => ({
      ...prevState,
      result: modifiedResult,
    }));

    calcCountOfChangedItems(modifiedResult);
  };

  const onChangeReason = (e) => {
    const modifiedResult = data.result.map((item) => {
      if (
        item.locationId === e.target.getAttribute('data-locationid') &&
        item.extendedUniqueId === e.target.getAttribute('data-packingslipid')
      ) {
        return {
          ...item,
          statusReason: e.target.value,
        };
      }

      return item;
    });

    // Update ref immediately for synchronous access
    dataResultRef.current = modifiedResult;

    setData((prevState) => ({
      ...prevState,
      result: modifiedResult,
    }));
  };

  const onClickCheckbox = (e) => {
    const currentLocationId = e.target.getAttribute('data-locationid');
    const currentPackingSlipId = e.target.getAttribute('data-packingslipid');
    const currentCheckboxState = e.target.checked;
    let checkedCount = 0;

    const modifiedResult = data.result.map((item) => {
      if (
        item.locationId === currentLocationId &&
        item.extendedUniqueId === currentPackingSlipId
      ) {
        // eslint-disable-next-line no-param-reassign
        item.checked = currentCheckboxState;
      }

      if (item.checked) {
        checkedCount += 1;
      }

      return item;
    });

    // Update ref immediately for synchronous access
    dataResultRef.current = modifiedResult;

    setData((prevState) => ({
      ...prevState,
      result: modifiedResult,
    }));
    setBulkUpdateDisabled(!checkedCount > 0);
    setCheckedBulkItemsCount(checkedCount);
  };

  const openBulkUpdateModal = () => {
    setShowBulkUpdateModal(true);
  };

  const sortArrayOfObjects = (arr, key, order) => {
    return arr.sort((a, b) => {
      if (order === 'asc') {
        return a[key] > b[key] ? 1 : -1;
      }
      return a[key] < b[key] ? 1 : -1;
    });
  };

  const onColumnSort = ({ key, order }) => {
    const sortedData = sortArrayOfObjects(data.result, key, order);
    setSortBy({ key, order });
    setData((prevState) => ({
      ...prevState,
      result: sortedData,
    }));

    // Update ref to sync with sorted results
    dataResultRef.current = sortedData;
  };

  const performSearch = (locationValue, routeValue) => {
    let searchedData = data.initResult;

    // Filter by location ID if locationValue is provided
    if (locationValue && locationValue.trim()) {
      const locationSearcher = new FuzzySearch(
        searchedData,
        ['locationId', 'displayLocationId'],
        {
          caseSensitive: false,
        },
      );
      searchedData = locationSearcher.search(locationValue);
    }

    // Filter by route if routeValue is provided
    if (routeValue && routeValue.trim()) {
      const routeSearcher = new FuzzySearch(searchedData, ['route'], {
        caseSensitive: false,
      });
      searchedData = routeSearcher.search(routeValue);
    }

    setData((prevState) => ({
      ...prevState,
      result: searchedData,
    }));

    // Update ref to sync with filtered results
    dataResultRef.current = searchedData;
  };

  const handleSearching = (e) => {
    const newLocationValue = e.target.value;
    setSearchbarValue(newLocationValue);
    performSearch(newLocationValue, routeSearchValue);
  };

  const handleRouteSearching = (e) => {
    const newRouteValue = e.target.value;
    setRouteSearchValue(newRouteValue);
    performSearch(searchbarValue, newRouteValue);
  };

  return (
    <>
      <PageHeader>
        Drop Off Summary
        {showData && <small className="">{`(${date.search})`}</small>}
      </PageHeader>
      {!showData ? (
        <Grid>
          {isInitLoading ? (
            <Loader />
          ) : (
            <>
              <Row className="text-center" style={{ marginBottom: '15px' }}>
                <div className="datePickerWrapper">
                  <div className="datePickerLabel">Drop Off Date</div>
                  <DatePicker
                    data-test="Date-Picker"
                    onChange={handleDateChange}
                    value={date.current}
                  />
                </div>
              </Row>
              <Row className="text-center" style={{ marginBottom: '15px' }}>
                <Button
                  bsStyle="primary"
                  bsSize="large"
                  disabled={isLoading}
                  onClick={handleSearch}
                >
                  {isLoading ? 'Loading...' : 'Search'}
                </Button>
              </Row>
            </>
          )}
        </Grid>
      ) : (
        <>
          {isLoading && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Loader />
            </div>
          )}
          <Button
            bsStyle="primary"
            bsSize="large"
            onClick={handleSearchClear}
            style={{ marginBottom: '15px', width: '100%' }}
          >
            Click to choose Another Date
          </Button>
          <h3>
            {`${data.result.length} Unconfirmed Drop${
              data.result.length === 1 ? '' : 's'
            }`}
          </h3>
          <div className="controlPanel">
            <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
              <input
                type="text"
                name="searchBox"
                className="searchBox"
                onChange={handleSearching}
                value={searchbarValue}
                placeholder="Search by Location ID"
              />
              <input
                type="text"
                name="routeSearchBox"
                className="searchBox"
                onChange={handleRouteSearching}
                value={routeSearchValue}
                placeholder="Search by Route ID"
              />
            </div>

            <span>
              <Button
                className="bulkUpdateBtn"
                bsSize="large"
                disabled={bulkUpdateDisabled}
                onClick={openBulkUpdateModal}
              >
                Bulk Update
              </Button>
              <Button
                className="btn-warning"
                bsSize="large"
                disabled={overrideDisabled}
                onClick={() => setShowConfirmModal(true)}
              >
                Confirm Override
              </Button>
            </span>
          </div>
          <div className="table-container">
            <BaseTable
              cellProps={{
                onClick: (e) => onClickTableEvent(e),
                onChange: (e) => onChangeTableEvent(e),
              }}
              data={data.result}
              columns={getDropOffSummaryColumns(onClickUndo, formatTimestamp)}
              rowKey="extendedUniqueId"
              width={1700}
              height={600}
              estimatedRowHeight={50}
              onColumnSort={onColumnSort}
              sortBy={sortBy}
              className="dropOffTable"
              rowClassName={({ rowData }) =>
                rowData.dropStatus !== 'Pending' && 'dropped'
              }
              fixed
            />
          </div>
        </>
      )}
      <DropOverrideBulkUpdateModal
        show={showBulkUpdateModal}
        onHide={() => setShowBulkUpdateModal(false)}
        onConfirm={(status, reason) => confirmOverride(true, status, reason)}
        checkedItems={checkedBulkItemsCount}
        loading={isLoading}
      />
      <ConfirmationModal
        title="Confirm status update"
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={() => confirmOverride(false, '', '')}
        loading={isLoading}
      >
        Please confirm if you would like to update the status of{' '}
        {modifiedStatusItemsCount} order
        {modifiedStatusItemsCount > 1 ? 's' : ''}.
      </ConfirmationModal>
      <ConfirmationModal
        title="Undo Drop Completion"
        show={showUndoConfirmModal}
        onHide={handleCancelUndo}
        onConfirm={handleConfirmUndo}
        loading={isLoading}
      >
        {itemToUndo && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              Are you sure you want to mark this drop as Pending?
            </div>
            <div
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={itemToUndo.displayLocationId}
            >
              <strong>Location:</strong> {itemToUndo.displayLocationId}
            </div>
            <div>
              <strong>Route:</strong> {itemToUndo.route}
            </div>
            <div>
              <strong>Packing Slip:</strong> {itemToUndo.uniqueId}
            </div>
          </div>
        )}
      </ConfirmationModal>
      <ConfirmationModal
        title={resultModalConfig.title}
        show={showResultModal}
        onHide={() => setShowResultModal(false)}
        onConfirm={() => {
          setShowResultModal(false);
          setShowUndoConfirmModal(true);
        }}
        confirmText="Continue Anyway"
        confirmButtonClass="btn-danger"
        confirmButtonStyle={{ fontSize: '12px', padding: '6px 12px' }}
        loading={isLoading}
      >
        {resultModalConfig.message}
      </ConfirmationModal>
    </>
  );
};

export default ViewDropOffSummary;
